import { z } from "zod";

export const membershipStatusSchema = z.enum(["none", "active", "expired", "canceled"]);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

export const storedMembershipStatusSchema = z.enum(["active", "expired", "canceled"]);
export type StoredMembershipStatus = z.infer<typeof storedMembershipStatusSchema>;

const timestamp = z.string().datetime();
const membershipIdSchema = z.string().trim().min(8).max(128);
const membershipLevelSchema = z.string().trim().min(1).max(32).regex(/^[A-Za-z0-9_-]+$/);

export function effectiveMembershipStatus(status: MembershipStatus, expiresAt: string | null, now = new Date()): MembershipStatus {
  if (status === "active" && expiresAt && new Date(expiresAt).getTime() <= now.getTime()) return "expired";
  return status;
}

export function isMembershipActive(status: MembershipStatus, expiresAt: string | null, now = new Date()): boolean {
  return effectiveMembershipStatus(status, expiresAt, now) === "active";
}

export const membershipSnapshotSchema = z.object({
  member: z.boolean(),
  status: membershipStatusSchema,
  level: z.string().min(1).max(64).regex(/^[A-Za-z0-9_.:-]+$/).nullable(),
  levelName: z.string().nullable().default(null),
  levelNameUg: z.string().nullable().default(null),
  expiresAt: timestamp.nullable(),
  startedAt: timestamp.nullable(),
  version: z.number().int().nonnegative(),
  inheritedFromAppId: z.string().max(128).nullable().default(null),
});
export type MembershipSnapshot = z.infer<typeof membershipSnapshotSchema>;

export const grantMembershipInputSchema = z.object({
  idempotencyKey: z.string().trim().min(8).max(128),
  level: membershipLevelSchema.default("member"),
  expiresAt: timestamp,
}).strict();
export type GrantMembershipInput = z.infer<typeof grantMembershipInputSchema>;

export const cancelMembershipInputSchema = z.object({
  expectedVersion: z.number().int().positive(),
}).strict();
export type CancelMembershipInput = z.infer<typeof cancelMembershipInputSchema>;

export const adminGrantMembershipInputSchema = grantMembershipInputSchema.extend({
  appId: z.string().trim().min(1).max(128),
  userId: z.string().trim().min(1).max(128),
}).strict();
export type AdminGrantMembershipInput = z.infer<typeof adminGrantMembershipInputSchema>;

export const membershipEventSchema = z.object({
  id: z.string().min(1),
  membershipId: membershipIdSchema,
  type: z.enum(["status", "notification"]),
  fromStatus: membershipStatusSchema.nullable(),
  toStatus: membershipStatusSchema.nullable(),
  eventKey: z.string().max(64).nullable(),
  channel: z.enum(["in_app"]).nullable(),
  note: z.string().max(500),
  createdAt: timestamp,
});
export type MembershipEvent = z.infer<typeof membershipEventSchema>;

export const adminMembershipSchema = z.object({
  id: membershipIdSchema,
  appId: z.string().min(1).max(128),
  appName: z.string().min(1),
  appCode: z.string().min(1),
  userId: z.string().min(1).max(128),
  userPublicId: z.string().regex(/^\d+$/),
  userName: z.string().min(1),
  userAvatarUrl: z.union([
    z.literal("/open/user/avatar"),
    z.string().regex(/^https:\/\//).max(500),
    z.null(),
  ]).optional(),
  member: z.boolean(),
  status: storedMembershipStatusSchema,
  level: z.string().min(1).max(64).regex(/^[A-Za-z0-9_.:-]+$/),
  levelName: z.string().nullable().default(null),
  levelNameUg: z.string().nullable().default(null),
  expiresAt: timestamp.nullable(),
  startedAt: timestamp,
  canceledAt: timestamp.nullable(),
  idempotencyKey: z.string().min(8).max(128),
  version: z.number().int().positive(),
  createdAt: timestamp,
  updatedAt: timestamp,
});
export type AdminMembership = z.infer<typeof adminMembershipSchema>;

export const adminMembershipDetailSchema = z.object({
  membership: adminMembershipSchema,
  events: z.array(membershipEventSchema),
});
export type AdminMembershipDetail = z.infer<typeof adminMembershipDetailSchema>;

export const adminMembershipListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  status: z.union([storedMembershipStatusSchema, z.literal("all")]).default("all"),
  appId: z.string().trim().max(128).default(""),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export type AdminMembershipListQuery = z.infer<typeof adminMembershipListQuerySchema>;

export const adminMembershipListResultSchema = z.object({
  items: z.array(adminMembershipSchema),
  total: z.number().int().nonnegative(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    expired: z.number().int().nonnegative(),
    canceled: z.number().int().nonnegative(),
  }),
});
export type AdminMembershipListResult = z.infer<typeof adminMembershipListResultSchema>;

export const membershipPlanSchema = z.object({
  appId: z.string().min(1).max(128),
  appName: z.string().min(1),
  appCode: z.string().min(1),
  level: membershipLevelSchema,
  name: z.string().trim().min(1).max(64),
  nameUg: z.string().trim().min(1).max(64),
  amountMinor: z.number().int().min(0).max(1_000_000_000).default(0),
  currency: z.string().regex(/^[A-Z]{3}$/).default("CNY"),
  periodDays: z.number().int().min(1).max(3650).nullable().default(null),
  period: z.string().trim().min(1).max(32).nullable().default(null),
  version: z.number().int().positive(),
  updatedAt: timestamp,
});
export type MembershipPlan = z.infer<typeof membershipPlanSchema>;

export const membershipPlanListQuerySchema = z.object({
  appId: z.string().trim().max(128).default(""),
}).strict();
export type MembershipPlanListQuery = z.infer<typeof membershipPlanListQuerySchema>;

export const membershipPlanListResultSchema = z.object({
  items: z.array(membershipPlanSchema),
});
export type MembershipPlanListResult = z.infer<typeof membershipPlanListResultSchema>;

export const membershipPlanUpsertSchema = z.object({
  appId: z.string().trim().min(1, "请选择关联应用").max(128),
  level: membershipLevelSchema,
  name: z.string().trim().min(1, "请输入中文名称").max(64),
  nameUg: z.string().trim().min(1, "请输入维吾尔语名称").max(64),
  amountMinor: z.number().int().min(0).max(1_000_000_000).default(0),
  currency: z.string().trim().regex(/^[A-Z]{3}$/).default("CNY"),
  periodDays: z.number().int().min(1).max(3650).nullable().optional(),
  period: z.string().trim().min(1).max(32).nullable().optional(),
  expectedVersion: z.number().int().positive().optional(),
}).strict();
export type MembershipPlanUpsertInput = z.input<typeof membershipPlanUpsertSchema>;
export const membershipPlanDeleteResultSchema = z.object({ ok: z.boolean() });
export type MembershipPlanDeleteResult = z.infer<typeof membershipPlanDeleteResultSchema>;

const appIdValue = z.string().trim().min(1).max(128);

export const membershipGrantGroupSchema = z.object({
  ownerAppId: appIdValue,
  ownerAppName: z.string().min(1),
  ownerAppCode: z.string().min(1),
  memberAppIds: z.array(appIdValue),
  memberApps: z.array(z.object({
    id: appIdValue,
    name: z.string().min(1),
    code: z.string().min(1),
  })),
  version: z.number().int().positive(),
  updatedAt: timestamp,
});
export type MembershipGrantGroup = z.infer<typeof membershipGrantGroupSchema>;

export const membershipGrantGroupListResultSchema = z.object({
  items: z.array(membershipGrantGroupSchema),
});
export type MembershipGrantGroupListResult = z.infer<typeof membershipGrantGroupListResultSchema>;

export const membershipGrantGroupUpsertSchema = z.object({
  ownerAppId: appIdValue,
  memberAppIds: z.array(appIdValue).max(32),
  expectedVersion: z.number().int().positive().optional(),
}).strict().superRefine((value, context) => {
  if (value.memberAppIds.includes(value.ownerAppId)) {
    context.addIssue({ code: "custom", path: ["memberAppIds"], message: "被授权应用不能包含主应用" });
  }
  if (new Set(value.memberAppIds).size !== value.memberAppIds.length) {
    context.addIssue({ code: "custom", path: ["memberAppIds"], message: "被授权应用不能重复" });
  }
});
export type MembershipGrantGroupUpsertInput = z.infer<typeof membershipGrantGroupUpsertSchema>;
export const membershipGrantGroupDeleteResultSchema = z.object({ ok: z.boolean() });
export type MembershipGrantGroupDeleteResult = z.infer<typeof membershipGrantGroupDeleteResultSchema>;

export * from "./assets";
export * from "./reconciliation";
export * from "./worker";
export * from "./statements";

export * from "./events";

export * from "./eventHealth";
